<script setup lang="ts">
import { nextTick, useTemplateRef, watch } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'

const {
  isEmpty,
  emptyMessage = 'No items found.',
  isFetching,
  hasNextPage,
  error = null,
  heightClass = 'h-60',
} = defineProps<{
  isEmpty: boolean
  emptyMessage?: string
  isFetching: boolean
  hasNextPage: boolean
  error?: string | null
  heightClass?: string
}>()

const emit = defineEmits<{
  (e: 'loadMore'): void
}>()

const scrollContainer = useTemplateRef('scroll-container')
useInfiniteScroll(
  scrollContainer,
  () => {
    if (!isFetching && hasNextPage) {
      emit('loadMore')
    }
  },
  {
    canLoadMore: () => hasNextPage,
  },
)

// useInfiniteScroll checks again only on a scroll or a visibility change, so a page
// that lands without filling the list left nothing to scroll and no next page (E2EF-01)
watch(
  () => [isFetching, hasNextPage] as const,
  async ([fetching, more]) => {
    if (fetching || !more) return
    await nextTick()
    const list = scrollContainer.value
    if (list && list.clientHeight > 0 && list.scrollHeight <= list.clientHeight) {
      emit('loadMore')
    }
  },
)
</script>

<template>
  <div ref="scroll-container" class="overflow-y-auto" :class="heightClass">
    <slot />

    <div
      v-if="isFetching || hasNextPage"
      class="flex h-12 items-center justify-center"
    >
      <ProgressSpinner stroke-width="8" class="h-8 w-8" />
    </div>

    <Message v-if="isEmpty && !isFetching" size="small"  class="m-2">{{
      emptyMessage
    }}</Message>

    <Message v-if="error" severity="error" size="small" class="m-1">{{
      error
    }}</Message>

    <Message
      v-if="!hasNextPage && !isEmpty"
      size="small"
      variant="simple"
      class="m-1"
      pt:content:class="justify-center"
    >
      No more items to load.
    </Message>
  </div>
</template>
